import React, { useRef } from 'react';
import type { IntlShape } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl';

import { Text } from '@atlaskit/primitives/compiled/text';
import type { ExtensionConfiguration } from '@atlaskit/editor-plugin-selection-extension/types';
import type { SelectionExtensionPlugin } from '@atlaskit/editor-plugin-selection-extension/selection-extension-plugin-type';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { createTrelloCardHandle } from '@confluence/highlight-actions/entry-points/createTrelloCardHandle.ts';
import { TrelloInboxIcon } from '@confluence/highlight-actions/entry-points/TrelloInboxIcon.ts';
import { trelloI18nMessages } from '@confluence/highlight-actions/entry-points/trelloI18nMessages.ts';
import { useTrelloAccess } from '@confluence/highlight-actions/entry-points/useTrelloAccess.ts';

const messages = defineMessages({
	menuItemLabel: {
		id: 'editor-features.trello-inbox.create-card',
		defaultMessage: 'Send to your Trello Inbox',
		description: 'Create a Trello card from highlighted text',
	},
});

const handleCreateTrelloCard = async (
	contentId: string,
	flags: FlagsStateContainer | undefined,
	formatMessage: IntlShape['formatMessage'],
) => {
	if (!flags) {
		return;
	}

	try {
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0) {
			return;
		}
		const selectedText = selection.toString().trim();
		if (!selectedText) {
			return;
		}
		const result = await createTrelloCardHandle({
			text: selectedText,
			contentId,
		});
		if (result?.cardUrl) {
			const titleNode = <Text>{formatMessage(trelloI18nMessages.successTitle)}</Text>;
			void flags.showFlag({
				id: `trello-inbox-create-success-${Date.now()}`,
				title: titleNode,
				appearance: 'success',
				actions: [
					{
						content: formatMessage(trelloI18nMessages.openCard),
						href: result.cardUrl!,
						target: '_blank',
					},
				],
			});
		} else {
			void flags.showFlag({
				id: `trello-inbox-create-error-${Date.now()}`,
				title: formatMessage(trelloI18nMessages.errorTitle),
				appearance: 'error',
			});
		}
	} catch {
		void flags.showFlag({
			id: `trello-inbox-create-error-${Date.now()}`,
			title: formatMessage(trelloI18nMessages.errorTitle),
			appearance: 'error',
		});
	}
};

export const useTrelloInboxSelectionExtension = (
	contentId: string | undefined,
	_editorAPIRef: React.RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>,
	flags?: FlagsStateContainer,
): ExtensionConfiguration => {
	const { formatMessage } = useIntl();
	const { hasTrelloAccess, loading } = useTrelloAccess();

	const accessRef = useRef({ hasTrelloAccess, loading });
	accessRef.current = { hasTrelloAccess, loading };

	return {
		key: 'trello-inbox',
		source: 'first-party',
		inlineToolbar: {
			getMenuItems: () => {
				if (!flags || accessRef.current.loading || !accessRef.current.hasTrelloAccess) {
					return [];
				}

				const label = formatMessage(messages.menuItemLabel);

				return [
					{
						label,
						icon: TrelloInboxIcon,
						onClick: () => handleCreateTrelloCard(contentId || '', flags, formatMessage),
					},
				];
			},
		},
	};
};
